import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePages, useStore } from './db/context';
import { AiPanel } from './ai/AiPanel';
import { ReorganizeDialog } from './components/ReorganizeDialog';
import { GraphView } from './components/GraphView';
import { useMcpBridge } from './mcp/bridge';
import { useClipInbox, useShareTarget } from './sync/clips';
import { TemplatesView } from './templates/TemplatesView';
import { useTemplatesFolderId } from './templates/folder';
import { MemosView } from './memos/MemosView';
import { TipsView } from './tips/TipsView';
import { useMemosFolderId } from './memos/data';
import { useRecurringTemplates } from './templates/recurring';
import { PluginsView } from './plugins/PluginsView';
import { PluginViewTab } from './plugins/PluginViewTab';
import { PluginConfirmDialog } from './plugins/PluginConfirmDialog';
import { PluginEditor } from './plugins/PluginEditor';
import { BuilderDialog } from './plugins/BuilderDialog';
import { NewPasswordDialog } from './sync/PasswordForms';
import { usePluginAutomations } from './plugins/automations';
import { UseTemplateDialog } from './templates/UseTemplateDialog';
import { TemplateSettingsDialog } from './templates/TemplateSettingsDialog';
import { McpConfirmDialog } from './mcp/McpConfirmDialog';
import { MovePageDialog } from './components/MovePageDialog';
import { useWorkspaces } from './workspaces/state';
import { CreateWorkspaceDialog } from './workspaces/CreateWorkspaceDialog';
import { ManageWorkspaceDialog } from './workspaces/ManageWorkspaceDialog';
import { useUi, viewKey } from './state/ui';
import { useIsMobile } from './lib/mobile';
import { Plus } from 'lucide-react';
import { SHORTCUT_KEYS, useShortcuts, type Shortcut } from './shortcuts';
import { cn } from './lib/cn';
import { Sidebar } from './components/Sidebar';
import { Tabs } from './components/Tabs';
import { PageView } from './components/PageView';
import { TrashView } from './components/TrashView';
import { TagView } from './components/TagView';
import { SearchView } from './components/SearchView';
import { ImportView } from './components/ImportView';
import { NexusImportView } from './components/NexusImportView';
import { TasksView } from './components/tasks/TasksView';
import { ProjectsView } from './components/tasks/ProjectsView';
import { DashboardView } from './components/dashboard/DashboardView';
import { QuickCapture } from './components/tasks/QuickCapture';
import {
  dedupeStarterDatabases,
  upgradeObsidianTemplates,
  ensureStarterDatabases,
  migrateTasks,
  upgradeStarterDatabases,
  upgradeStarterDatabasesV3,
} from '@nexus/workspace';
import {
  handleNotificationAction,
  openReminder,
  useReminderScheduler,
  type NotificationActionKind,
} from './notifications/scheduler';
import { ToastHost } from './notifications/NotificationCenter';
import { applyDesktopPrefs } from './notifications/desktop';
import { useSync, useSyncState } from './sync/useSync';
import { usePushSchedule } from './push/usePushSchedule';
import { useCatalogSync } from './state/catalog';
import { ensureDailyNote } from './lib/pages';
import { CommandPalette } from './components/CommandPalette';
import { SettingsDialog } from './components/SettingsDialog';
import { Welcome } from './components/Welcome';
import { SidebarMenuDialog } from './components/SidebarMenuDialog';
import { TitleBar } from './desktop/TitleBar';
import { PagePeek } from './components/PagePeek';
import { PwaBanner, StorageBanner } from './components/Banners';
import { UpdateBanner } from './desktop/updates';
import { useDesktopUpdates } from './desktop/updater';

const isDesktop = '__TAURI_INTERNALS__' in window;

/** Deep links: `#/p/<pageId>` opens a page, `#/new` (PWA shortcut) creates one. */
function useHashLinks() {
  const store = useStore();
  const ui = useUi();
  const current = ui.tabs[ui.active];

  useEffect(() => {
    const handle = async () => {
      const hash = window.location.hash;
      if (hash === '#/new') {
        const page = await store.createPage({ title: '' });
        useUi.getState().open({ kind: 'page', pageId: page.id }, true);
      } else if (hash.startsWith('#/p/')) {
        useUi.getState().open({ kind: 'page', pageId: hash.slice(4) });
      } else if (hash.startsWith('#/notification/')) {
        // A browser notification clicked while the app was closed.
        const [id, action] = hash
          .slice('#/notification/'.length)
          .split('/')
          .map(decodeURIComponent);
        const n = (await store.listNotifications(500)).find((x) => x.id === id);
        if (n)
          await handleNotificationAction(store, n, (action as NotificationActionKind) || 'open');
        else useUi.getState().set({ notificationsOpen: true });
      } else if (hash.startsWith('#/reminder/')) {
        // A pushed reminder clicked while the app was closed.
        const [pageId, taskId] = hash
          .slice('#/reminder/'.length)
          .split('/')
          .map(decodeURIComponent);
        if (pageId) openReminder(pageId, taskId);
      } else if (hash === '#/notifications') {
        useUi.getState().set({ notificationsOpen: true });
      }
    };
    void handle();
    window.addEventListener('hashchange', handle);
    return () => window.removeEventListener('hashchange', handle);
  }, [store]);

  useEffect(() => {
    const next = current?.kind === 'page' ? `#/p/${current.pageId}` : '';
    if (window.location.hash !== next)
      history.replaceState(null, '', next || window.location.pathname);
  }, [current]);
}

export function App() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const pages = usePages();
  const workspaces = useWorkspaces();
  const ui = useUi();
  const current = ui.tabs[ui.active];
  const mobile = useIsMobile();
  const templatesFolderId = useTemplatesFolderId();
  const memosFolderId = useMemosFolderId();

  // Phones: choosing a page or view in the drawer closes it.
  const currentKey = current ? viewKey(current) : null;
  useEffect(() => {
    useUi.getState().set({ drawerOpen: false });
  }, [currentKey]);

  useHashLinks();
  useReminderScheduler();
  useRecurringTemplates();
  usePluginAutomations();
  useSync();
  usePushSchedule();
  useDesktopUpdates();
  useMcpBridge();
  useClipInbox();
  useShareTarget((pageId) => useUi.getState().open({ kind: 'page', pageId }, true));
  useEffect(() => {
    void applyDesktopPrefs(store);
  }, [store]);
  useCatalogSync(store, pages?.list);

  // Close tabs whose pages were trashed or deleted.
  useEffect(() => {
    if (pages) ui.pruneTabs(new Set(pages.list.map((p) => p.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  // One-time upgrade of older workspaces: task ids + task index.
  useEffect(() => {
    void (async () => {
      if (await store.getSetting('tasks_migrated_v1')) return;
      await migrateTasks(store);
      await store.setSetting('tasks_migrated_v1', String(Date.now()));
    })();
  }, [store]);

  // Starter databases (Taken, Projecten, Vergaderingen), once per workspace. When
  // signed in, wait for the first sync: another device may have made them already.
  // A device used signed out made its own; after each sync, duplicates from other
  // devices are merged into one set.
  const authReady = useSyncState((s) => s.authReady);
  const signedIn = useSyncState((s) => !!s.session);
  const lastSyncAt = useSyncState((s) => s.status.lastSyncAt);
  const synced = !!lastSyncAt;
  const starterBusy = useRef(false);
  useEffect(() => {
    if (!authReady || (signedIn && !synced) || starterBusy.current) return;
    starterBusy.current = true;
    void (async () => {
      if (!(await store.getSetting('starter_databases_v1'))) {
        await ensureStarterDatabases(store); // includes the v2 additions
        await store.setSetting('starter_databases_v1', String(Date.now()));
        await store.setSetting('starter_databases_v2', String(Date.now()));
        await store.setSetting('starter_databases_v3', String(Date.now()));
      } else if (!(await store.getSetting('starter_databases_v2'))) {
        // Fase 5b: relations, progress, days left, recurring button.
        await upgradeStarterDatabases(store);
        await store.setSetting('starter_databases_v2', String(Date.now()));
      }
      if (!(await store.getSetting('starter_databases_v3'))) {
        // Dropdowns for Herhaling and Eigenaar, Gepland and Startdatum dates.
        await upgradeStarterDatabasesV3(store);
        await store.setSetting('starter_databases_v3', String(Date.now()));
      }
      if (signedIn) await dedupeStarterDatabases(store);
      await upgradeObsidianTemplates(store);
    })().finally(() => {
      starterBusy.current = false;
    });
  }, [store, authReady, signedIn, synced, lastSyncAt]);

  // Reveal the active page in the sidebar: expand its ancestors.
  const activeId = current?.kind === 'page' ? current.pageId : null;
  // Recently visited pages (Ctrl+K / Ctrl+O when nothing is typed yet).
  useEffect(() => {
    if (activeId) useUi.getState().visit(activeId);
  }, [activeId]);
  useEffect(() => {
    if (!pages || !activeId) return;
    const { expanded, toggleExpanded } = useUi.getState();
    let parent = pages.byId.get(activeId)?.parentId;
    while (parent) {
      if (!expanded[parent]) toggleExpanded(parent, true);
      parent = pages.byId.get(parent)?.parentId;
    }
  }, [activeId, pages]);

  const shortcuts = useMemo<Shortcut[]>(
    () => [
      {
        id: 'palette',
        keys: SHORTCUT_KEYS.palette,
        run: () => useUi.getState().set({ paletteOpen: true, paletteMode: 'all' }),
      },
      {
        id: 'quickSwitcher',
        keys: SHORTCUT_KEYS.quickSwitcher,
        run: () => useUi.getState().set({ paletteOpen: true, paletteMode: 'pages' }),
      },
      {
        id: 'capture',
        keys: SHORTCUT_KEYS.capture,
        run: () => useUi.getState().set({ captureOpen: true }),
      },
      {
        id: 'tasks',
        keys: SHORTCUT_KEYS.tasks,
        run: () => useUi.getState().open({ kind: 'tasks', tab: 'today' }),
      },
      {
        id: 'search',
        keys: SHORTCUT_KEYS.search,
        run: () => useUi.getState().open({ kind: 'search', query: '' }),
      },
      {
        id: 'dailyNote',
        keys: SHORTCUT_KEYS.dailyNote,
        run: async () => {
          const page = await ensureDailyNote(store, i18n.language);
          useUi.getState().open({ kind: 'page', pageId: page.id });
        },
      },
      {
        id: 'newPage',
        keys: SHORTCUT_KEYS.newPage,
        run: async () => {
          const page = await store.createPage({ title: '' });
          useUi.getState().open({ kind: 'page', pageId: page.id }, true);
        },
      },
      {
        id: 'closeTab',
        keys: SHORTCUT_KEYS.closeTab,
        run: () => useUi.getState().closeTab(useUi.getState().active),
      },
      {
        id: 'toggleSidebar',
        keys: SHORTCUT_KEYS.toggleSidebar,
        run: () => {
          const s = useUi.getState();
          if (window.matchMedia('(max-width: 767px)').matches) s.set({ drawerOpen: !s.drawerOpen });
          else s.set({ sidebarOpen: !s.sidebarOpen });
        },
      },
      {
        id: 'settings',
        keys: SHORTCUT_KEYS.settings,
        run: () => useUi.getState().set({ settingsOpen: true }),
      },
      {
        id: 'ai',
        keys: SHORTCUT_KEYS.ai,
        run: () => useUi.getState().set({ aiOpen: !useUi.getState().aiOpen }),
      },
    ],
    [store, i18n.language],
  );
  useShortcuts(shortcuts);

  if (!pages) return null;

  const activePageId = current?.kind === 'page' ? current.pageId : null;
  const activePage = activePageId ? pages.byId.get(activePageId) : undefined;
  const activePageWorkspacePermission = activePage?.workspaceId
    ? (workspaces.find((w) => w.id === activePage.workspaceId)?.permission ?? null)
    : null;

  return (
    <div className="flex h-full flex-col">
      {isDesktop && <TitleBar />}
      <StorageBanner />
      {!isDesktop && <PwaBanner />}
      {isDesktop && <UpdateBanner />}
      <div className="flex min-h-0 flex-1">
        {mobile
          ? ui.drawerOpen && (
              <div className="fixed inset-0 z-40 flex pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] md:hidden">
                <Sidebar tree={pages.tree} activePageId={activePageId} />
                <button
                  type="button"
                  aria-label={t('sidebar.close')}
                  onClick={() => ui.set({ drawerOpen: false })}
                  className="flex-1 bg-black/30"
                />
              </div>
            )
          : ui.sidebarOpen && <Sidebar tree={pages.tree} activePageId={activePageId} />}
        {/* The page as a white sheet on the warm canvas (full screen on phones). */}
        <main
          className={cn(
            'flex min-w-0 flex-1 flex-col bg-bg md:my-2 md:mr-2 md:overflow-hidden md:rounded-sheet md:shadow-sheet',
            !ui.sidebarOpen && 'md:ml-2',
          )}
        >
          <Tabs byId={pages.byId} />
          <div className="min-h-0 flex-1 overflow-y-auto">
            {current?.kind === 'trash' ? (
              <TrashView />
            ) : current?.kind === 'tag' ? (
              <TagView tag={current.tag} />
            ) : current?.kind === 'search' ? (
              <SearchView key={current.query} initial={current.query} />
            ) : current?.kind === 'import' ? (
              <ImportView />
            ) : current?.kind === 'nexus-import' ? (
              <NexusImportView />
            ) : current?.kind === 'tasks' ? (
              <TasksView tab={current.tab} />
            ) : current?.kind === 'projects' ? (
              <ProjectsView />
            ) : current?.kind === 'dashboard' ? (
              <DashboardView />
            ) : current?.kind === 'graph' ? (
              <GraphView key={current.center ?? 'all'} center={current.center ?? null} />
            ) : current?.kind === 'plugins' ? (
              <PluginsView />
            ) : current?.kind === 'plugin' ? (
              <PluginViewTab
                key={`${current.pluginId}:${current.viewId}`}
                pluginId={current.pluginId}
                viewId={current.viewId}
              />
            ) : current?.kind === 'tips' ? (
              <TipsView />
            ) : current?.kind === 'memos' || (activePage && activePage.id === memosFolderId) ? (
              <MemosView />
            ) : current?.kind === 'templates' ||
              (activePage && activePage.id === templatesFolderId) ? (
              // The hidden "Sjablonen" folder opens as the template overview.
              <TemplatesView />
            ) : activePage ? (
              <PageView
                key={activePage.id}
                page={activePage}
                children={pages.list.filter((p) => p.parentId === activePage.id)}
                readOnly={activePageWorkspacePermission === 'read'}
              />
            ) : activePageId ? null /* page still loading, or about to be pruned */ : (
              // No tab open: the dashboard is the start page.
              <DashboardView />
            )}
          </div>
        </main>
        {ui.aiOpen && <AiPanel />}
      </div>
      {mobile && !ui.drawerOpen && !ui.aiOpen && (
        <button
          type="button"
          aria-label={t('capture.title')}
          onClick={() => ui.set({ captureOpen: true })}
          className="fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-30 grid size-14 place-items-center rounded-full bg-accent text-accent-fg shadow-pop"
        >
          <Plus className="size-6" />
        </button>
      )}
      <CommandPalette />
      <SettingsDialog />
      <Welcome />
      <SidebarMenuDialog />
      <PagePeek />
      <ReorganizeDialog />
      <McpConfirmDialog />
      <MovePageDialog />
      <CreateWorkspaceDialog />
      <ManageWorkspaceDialog />
      <UseTemplateDialog />
      <TemplateSettingsDialog />
      <PluginConfirmDialog />
      <PluginEditor />
      <BuilderDialog />
      <NewPasswordDialog />
      <QuickCapture />
      <ToastHost />
    </div>
  );
}
